import { apiClient } from "@/lib/api";
import type { ChannelChange, OrderPriority, RepeatResult } from "./orders.api";
import type { FieldMap, ListRowInput, MailList, PlaceRulesView } from "./listRows";

// Orders inlezen (docs/tms-orderintake.md §3g, docs/api.md §9.60): the mail channels — a list template per customer, with
// whose mail it reads and which column fills which field — the lists in a mail, and the orders made from one.

/** A stop of the standard order of an ordermail: its place (none when the list names it per row) and what every order says there. */
export interface StandardStopView {
  location: { id: string; name: string | null; city: string | null } | null;
  reference: string | null;
  instruction: string | null;
  contactName: string | null;
  contactPhone: string | null;
  requirements: string[];
}

/** What every order of an ordermail has unless the list or the mail says otherwise (25-10-2026). */
export interface StandardOrder {
  priority: OrderPriority;
  description: string | null;
  remark: string | null;
  /** The loading metres of an order whose mail says none. */
  loadingMeters: number | null;
  requirementIds: string[];
  load: StandardStopView;
  unload: StandardStopView;
  /** The sender in box 1 of the CMR, with its place when chosen from Locaties; null: the loading address. */
  shipper: { locationId: string | null; name: string; city: string | null } | null;
}

export interface StandardStopInput {
  locationId: string | null;
  reference: string | null;
  instruction: string | null;
  contactName: string | null;
  contactPhone: string | null;
  requirements: string[];
}

export interface OrderListTemplate {
  id: string;
  name: string;
  party: { id: string; code: string; name: string };
  /** The order the goods, places and requirements were taken from. */
  sourceOrderNumber: string | null;
  /** What every reference starts with; {week} is the ISO week of the list. */
  referencePrefix: string | null;
  metresPerPallet: number | null;
  provisional: boolean;
  /** Not to be invoiced, with this reason; null: invoiced as any order. */
  noInvoiceReason: string | null;
  /** Whose mail the channel reads: an address, or "@domain". */
  senders: string[];
  /** Which column fills which field, by its header; remembered after every list. */
  fields: FieldMap;
  loadDateRule: "previous_working_day" | null;
  metresByCarrier: Record<string, number>;
  rowFilter: { header: string; contains: string } | null;
  placeRules: PlaceRulesView;
  /** The ordermail (docs/invoerbak.md §3b): only mail whose subject holds this, into the bak by itself, read as. */
  subjectFilter: string | null;
  autoIntake: boolean;
  readAs: "list" | "mail";
  proposeWarehouse: boolean;
  goods: { description: string | null; pallets: number | null; loadingMeters: number | null };
  load: { name: string; city: string } | null;
  unload: { name: string; city: string } | null;
  standard: StandardOrder;
  changedAt: string | null;
  changedBy: string | null;
}

export interface OrderListTemplateInput {
  name?: string;
  sourceOrderNumber?: string | null;
  partyId?: string | null;
  subjectFilter?: string | null;
  autoIntake?: boolean;
  readAs?: "list" | "mail";
  proposeWarehouse?: boolean;
  referencePrefix?: string | null;
  metresPerPallet?: number | null;
  provisional?: boolean;
  noInvoiceReason?: string | null;
  senders?: string[];
  fields?: FieldMap;
  loadDateRule?: "previous_working_day" | null;
  metresByCarrier?: Record<string, number>;
  rowFilter?: { header: string; contains: string } | null;
  placeRules?: Partial<Record<"load" | "unload", { header: string; places: { contains: string; locationId: string }[] }>>;
  standard?: {
    priority: OrderPriority;
    description: string | null;
    remark: string | null;
    /** Left out: kept as it is (a list has its metres per pallet). */
    loadingMeters?: number | null;
    requirementIds: string[];
    load: StandardStopInput;
    unload: StandardStopInput;
    /** The sender in box 1 of the CMR, a place of Locaties; null: none; left out: as kept. */
    shipperLocationId?: string | null;
  };
}

export function listOrderListTemplates(partyId?: string): Promise<OrderListTemplate[]> {
  return apiClient.get<OrderListTemplate[]>(`/api/v1/order-templates${partyId ? `?partyId=${encodeURIComponent(partyId)}` : ""}`);
}

export function createOrderListTemplate(input: OrderListTemplateInput & { name: string }): Promise<OrderListTemplate> {
  return apiClient.post<OrderListTemplate>("/api/v1/order-templates", input);
}

export function updateOrderListTemplate(id: string, input: OrderListTemplateInput): Promise<OrderListTemplate> {
  return apiClient.patch<OrderListTemplate>(`/api/v1/order-templates/${id}`, input);
}

export function deleteOrderListTemplate(id: string): Promise<void> {
  return apiClient.delete<void>(`/api/v1/order-templates/${id}`);
}

/** The lists in a mail and the channel of its sender. */
export interface MailLists {
  mail: { subject: string; from: string | null; fromName: string | null; forwardedFrom: string | null; receivedAt: string | null };
  lists: MailList[];
  dates: { load: string | null; unload: string | null; week: string | null };
  channelId: string | null;
  unread: string[];
}

/** A shared mailbox by its id, or "me". */
export function readMailLists(mailboxId: string, messageId: string): Promise<MailLists> {
  const path =
    mailboxId === "me"
      ? `/api/v1/mail/me/messages/${encodeURIComponent(messageId)}/order-lists`
      : `/api/v1/mail/mailboxes/${encodeURIComponent(mailboxId)}/messages/${encodeURIComponent(messageId)}/order-lists`;
  return apiClient.get<MailLists>(path);
}

/** The orders of a list: a row each. The mail is kept with them, and the columns are remembered on the channel. */
export function ordersFromTemplate(
  id: string,
  body: {
    rows: ListRowInput[];
    provisional: boolean;
    noInvoiceReason: string | null;
    fields: FieldMap;
    mail: { mailbox: string; messageId: string };
  },
): Promise<RepeatResult> {
  return apiClient.post<RepeatResult>(`/api/v1/order-templates/${id}/orders`, body);
}

/**
 * A row of a list held against the customer's orders by its reference, before anything is made: new, changed (with what
 * it changes, and held when the order is on a trip on the road), unchanged, closed (done with or called off) or invalid.
 */
export interface RowComparison {
  index: number;
  kind: "new" | "changed" | "unchanged" | "closed" | "invalid";
  order: { id: string; number: string; status: string; trips: { tripNumber: string; status: string }[] } | null;
  changes: ChannelChange[];
  held: boolean;
  error?: { code: string; message: string };
}

/** An example list from a file an admin chooses (Excel or CSV), read as a mail's attachment is and not kept. */
export function readSampleFile(file: File): Promise<MailLists> {
  return apiClient.postRaw<MailLists>(`/api/v1/order-templates/sample-file?name=${encodeURIComponent(file.name)}`, file, file.type || "application/octet-stream");
}

/** Each row against the customer's orders, writing nothing. */
export function compareListRows(id: string, rows: ListRowInput[]): Promise<RowComparison[]> {
  return apiClient.post<RowComparison[]>(`/api/v1/order-templates/${id}/compare`, { rows });
}

export const orderListTemplateKeys = {
  all: ["order-templates"] as const,
  party: (partyId: string) => ["order-templates", "party", partyId] as const,
  mail: (mailboxId: string, messageId: string) => ["order-templates", "mail", mailboxId, messageId] as const,
  compare: (id: string, rows: string) => ["order-templates", "compare", id, rows] as const,
};
